import React, { useState, useEffect } from 'react';
import { X, Save, Eye, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import reportConfigurationService, {
  ReportConfiguration,
  StandardReportInfo,
  ReportType,
  ReportTypeLabels
} from '../../../../services/reportConfigurationService';
import printNodeService from '../../../../services/printNodeService';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import type { Printer as PrinterType } from '../../../../types/printNode';

interface ReportConfigurationModalProps {
  isOpen: boolean;
  onClose: (refresh: boolean) => void;
  report: ReportConfiguration | null;
}

export const ReportConfigurationModal: React.FC<ReportConfigurationModalProps> = ({
  isOpen,
  onClose,
  report
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo;
  const isEditMode = !!report;

  // Form state
  const [formData, setFormData] = useState<Partial<ReportConfiguration>>({
    reportID: 0,
    reportType: ReportType.ItemLabel,
    reportName: '',
    description: '',
    printerName: '',
    cloudStorageYesNo: false,
    cloudStorageItemLabelFolder: '',
    externalShare: '',
    externalFilemask: '',
    externalPrinterWidth: 100,
    externalPrinterLength: 50,
    externalPrinterLandscape: false
  });

  // Dropdown data
  const [standardReports, setStandardReports] = useState<StandardReportInfo[]>([]);
  const [printers, setPrinters] = useState<PrinterType[]>([]);
  const [loadingStandardReports, setLoadingStandardReports] = useState(false);
  const [loadingPrinters, setLoadingPrinters] = useState(false);

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [useCustomReportName, setUseCustomReportName] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      if (report) {
        setFormData(report);
        setUseCustomReportName(!report.reportName.startsWith('STANDARD:'));
      } else {
        resetForm();
      }
      loadStandardReports();
      loadPrinters();
    }
  }, [isOpen, report]);

  useEffect(() => {
    if (formData.reportType) {
      loadStandardReportsByType(formData.reportType);
    }
  }, [formData.reportType]);

  const resetForm = () => {
    setFormData({
      reportID: 0,
      reportType: ReportType.ItemLabel,
      reportName: '',
      description: '',
      printerName: '',
      cloudStorageYesNo: false,
      cloudStorageItemLabelFolder: '',
      externalShare: '',
      externalFilemask: '',
      externalPrinterWidth: 100,
      externalPrinterLength: 50,
      externalPrinterLandscape: false
    });
    setUseCustomReportName(false);
    setError(null);
    setValidationErrors([]);
  };

  const loadStandardReports = async () => {
    if (!companyNo) return;

    setLoadingStandardReports(true);
    try {
      const reports = await reportConfigurationService.getStandardReportCatalog(companyNo);
      setStandardReports(reports);
    } catch (err) {
      console.error('Failed to load standard reports:', err);
    } finally {
      setLoadingStandardReports(false);
    }
  };

  const loadStandardReportsByType = async (reportType: number) => {
    if (!companyNo) return;

    setLoadingStandardReports(true);
    try {
      const reports = await reportConfigurationService.getStandardReportsByType(companyNo, reportType);
      setStandardReports(reports);
    } catch (err) {
      console.error('Failed to load standard reports by type:', err);
    } finally {
      setLoadingStandardReports(false);
    }
  };

  const loadPrinters = async () => {
    if (!companyNo) return;

    setLoadingPrinters(true);
    try {
      const printerList = await printNodeService.getPrinters(companyNo);
      setPrinters(printerList);
    } catch (err) {
      console.error('Failed to load printers:', err);
    } finally {
      setLoadingPrinters(false);
    }
  };

  const handleStandardReportChange = (reportName: string) => {
    const template = standardReports.find(r => r.reportName === reportName);
    if (template) {
      setFormData(prev => ({
        ...prev,
        reportName: template.reportName,
        description: prev.description || template.displayName,
        externalPrinterWidth: template.paperWidth,
        externalPrinterLength: template.paperHeight,
        externalPrinterLandscape: template.landscape
      }));
    }
  };

  const handlePreview = async () => {
    if (!companyNo || !formData.reportName || !formData.reportName.startsWith('STANDARD:')) {
      return;
    }

    try {
      const blob = await reportConfigurationService.getStandardReportPreview(
        companyNo,
        formData.reportName
      );
      const url = URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      setShowPreview(true);
    } catch (err: any) {
      setError(t('manager.modules.adminReports.modal.previewFailed', { '0': err.message }, `Forhåndsvisning feilet: ${err.message}`));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyNo) return;

    setError(null);
    setValidationErrors([]);
    setSaving(true);

    try {
      // Validate - ensure companyNo is included
      const dataToValidate = {
        ...formData,
        companyNo: companyNo || ''
      };

      const validation = await reportConfigurationService.validate(companyNo || '', dataToValidate);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setSaving(false);
        return;
      }

      // Save
      if (isEditMode && report) {
        await reportConfigurationService.update(companyNo || '', report.id, {
          ...report,
          ...formData,
          companyNo: companyNo || ''
        } as ReportConfiguration);
      } else {
        await reportConfigurationService.create(companyNo || '', {
          ...formData,
          companyNo: companyNo || ''
        });
      }

      onClose(true); // Refresh list
    } catch (err: any) {
      console.error('Failed to save report:', err);
      setError(err.message || t('manager.modules.adminReports.modal.saveError', {}, 'Kunne ikke lagre rapport'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? t('manager.modules.adminReports.modal.editTitle', {}, 'Rediger Rapport') : t('manager.modules.adminReports.modal.newTitle', {}, 'Ny Rapport')}
            </h2>
            <button
              onClick={() => onClose(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Error Messages */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="font-medium text-yellow-900 mb-2">{t('manager.modules.adminReports.modal.validationErrors', {}, 'Valideringsfeil:')}</p>
                <ul className="list-disc list-inside text-yellow-800 space-y-1">
                  {validationErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* ReportID (read-only in edit mode) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('manager.modules.adminReports.modal.reportIdLabel', {}, 'Rapport ID')}
                </label>
                <input
                  type="number"
                  value={formData.reportID || ''}
                  onChange={(e) => setFormData({ ...formData, reportID: parseInt(e.target.value) || 0 })}
                  disabled={isEditMode}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  required
                />
              </div>

              {/* ReportType */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('manager.modules.adminReports.modal.reportTypeLabel', {}, 'Rapport Type *')}
                </label>
                <select
                  value={formData.reportType}
                  onChange={(e) => setFormData({ ...formData, reportType: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {Object.entries(ReportTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('manager.modules.adminReports.modal.descriptionLabel', {}, 'Beskrivelse *')}
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('manager.modules.adminReports.modal.descriptionPlaceholder', {}, "F.eks. 'Vareetikett med batch 100x50mm'")}
                required
              />
            </div>

            {/* Report Name Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('manager.modules.adminReports.modal.reportNameLabel', {}, 'Rapportnavn *')}
              </label>

              <div className="flex items-center gap-4 mb-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={!useCustomReportName}
                    onChange={() => setUseCustomReportName(false)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">{t('manager.modules.adminReports.modal.standardReportRadio', {}, 'Standard rapport')}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={useCustomReportName}
                    onChange={() => setUseCustomReportName(true)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">{t('manager.modules.adminReports.modal.customReportRadio', {}, 'Egendefinert rapport')}</span>
                </label>
              </div>

              {!useCustomReportName ? (
                <div className="flex gap-2">
                  <select
                    value={formData.reportName}
                    onChange={(e) => handleStandardReportChange(e.target.value)}
                    disabled={loadingStandardReports}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">{t('manager.modules.adminReports.modal.selectStandardReport', {}, 'Velg standard rapport...')}</option>
                    {standardReports.map((report) => (
                      <option key={report.reportName} value={report.reportName}>
                        {report.displayName}
                      </option>
                    ))}
                  </select>
                  {formData.reportName && formData.reportName.startsWith('STANDARD:') && (
                    <button
                      type="button"
                      onClick={handlePreview}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                    >
                      <Eye size={18} />
                      {t('manager.modules.adminReports.modal.preview', {}, 'Forhåndsvisning')}
                    </button>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  value={formData.reportName}
                  onChange={(e) => setFormData({ ...formData, reportName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('manager.modules.adminReports.modal.customReportPlaceholder', {}, "F.eks. 'CustomLabel.fr3' eller '\\\\server\\share\\MyReport.rdlc'")}
                  required
                />
              )}
            </div>

            {/* Printer Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('manager.modules.adminReports.modal.printerLabel', {}, 'Printer')}
              </label>
              <select
                value={formData.printerName}
                onChange={(e) => setFormData({ ...formData, printerName: e.target.value })}
                disabled={loadingPrinters}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('manager.modules.adminReports.modal.noPrinterSelected', {}, 'Ingen printer valgt')}</option>
                {printers.map((printer) => (
                  <option key={printer.id} value={printer.name}>
                    {printer.name} {printer.description && `- ${printer.description}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Dimensions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('manager.modules.adminReports.modal.dimensionsLabel', {}, 'Dimensjoner')}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('manager.modules.adminReports.modal.widthMm', {}, 'Bredde (mm)')}</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.externalPrinterWidth || ''}
                    onChange={(e) => setFormData({ ...formData, externalPrinterWidth: parseFloat(e.target.value) || undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('manager.modules.adminReports.modal.lengthMm', {}, 'Lengde (mm)')}</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.externalPrinterLength || ''}
                    onChange={(e) => setFormData({ ...formData, externalPrinterLength: parseFloat(e.target.value) || undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="50"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.externalPrinterLandscape || false}
                      onChange={(e) => setFormData({ ...formData, externalPrinterLandscape: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700">{t('manager.modules.adminReports.modal.landscape', {}, 'Liggende')}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Cloud Storage */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.cloudStorageYesNo || false}
                  onChange={(e) => setFormData({ ...formData, cloudStorageYesNo: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminReports.modal.cloudStorageLabel', {}, 'Lagre i skylagring')}</span>
              </label>

              {formData.cloudStorageYesNo && (
                <input
                  type="text"
                  value={formData.cloudStorageItemLabelFolder || ''}
                  onChange={(e) => setFormData({ ...formData, cloudStorageItemLabelFolder: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('manager.modules.adminReports.modal.cloudFolderPlaceholder', {}, 'Mappe i skylagring')}
                />
              )}
            </div>

            {/* External Report Settings (only for External type) */}
            {formData.reportType === ReportType.External && (
              <div className="space-y-4 border-t pt-4">
                <h3 className="font-medium text-gray-900">{t('manager.modules.adminReports.modal.externalSettingsTitle', {}, 'Eksterne Rapport-innstillinger')}</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.adminReports.modal.externalShareLabel', {}, 'Ekstern Share/Sti *')}
                  </label>
                  <input
                    type="text"
                    value={formData.externalShare || ''}
                    onChange={(e) => setFormData({ ...formData, externalShare: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="\\\\server\\share\\reports"
                    required={formData.reportType === ReportType.External}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.adminReports.modal.fileMaskLabel', {}, 'Filmaske *')}
                  </label>
                  <input
                    type="text"
                    value={formData.externalFilemask || ''}
                    onChange={(e) => setFormData({ ...formData, externalFilemask: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Label_V:[ITEMNO].pdf"
                    required={formData.reportType === ReportType.External}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('manager.modules.adminReports.modal.fileMaskHint', {}, 'Bruk V:[ITEMNO] for variabel-substitusjon')}
                  </p>
                </div>
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={() => onClose(false)}
              disabled={saving}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {t('common.buttons.cancel', {}, 'Avbryt')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  {t('common.status.saving', {}, 'Lagrer...')}
                </>
              ) : (
                <>
                  <Save size={18} />
                  {t('common.buttons.save', {}, 'Lagre')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && previewPdfUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{t('manager.modules.adminReports.modal.preview', {}, 'Forhåndsvisning')}</h3>
              <button
                onClick={() => {
                  setShowPreview(false);
                  URL.revokeObjectURL(previewPdfUrl);
                  setPreviewPdfUrl('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={previewPdfUrl}
                className="w-full h-full"
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
